/**
 * Geo Mismatch Banner
 * When a visitor lands on a market-locked locale (e.g. /ja-jp/, /en-jp/)
 * but Shopify's IP geolocation says they're NOT actually in that country,
 * show a dismissable banner offering to switch to their local market.
 *
 * Typical trigger: ChatGPT / Perplexity / Bing Chat surface the ja-JP
 * hreflang version to a non-Japanese user who ends up paying JPY.
 */
(function () {
  'use strict';

  // Which locale paths should trigger this banner. Each entry maps path prefix
  // -> { marketCountry, marketCurrency, marketLabel } that we're locked into.
  var LOCKED_MARKETS = {
    '/ja-jp': { country: 'JP', currency: 'JPY', label: 'Japan' },
    '/en-jp': { country: 'JP', currency: 'JPY', label: 'Japan' }
  };

  var DISMISS_KEY = 'geo_mismatch_dismissed_at';
  var DISMISS_TTL_MS = 7 * 24 * 3600 * 1000; // 7 days

  // Skip on cart/checkout/account pages to avoid interrupting purchase flow
  var SKIP_PATTERNS = ['/cart', '/checkout', '/checkouts', '/account', '/orders', '/password'];

  function init() {
    if (!window.Shopify || !window.Shopify.routes) return;

    var path = window.location.pathname;

    for (var i = 0; i < SKIP_PATTERNS.length; i++) {
      if (path.indexOf(SKIP_PATTERNS[i]) !== -1) return;
    }

    // Detect locked market from path
    var locked = null;
    for (var prefix in LOCKED_MARKETS) {
      if (path.indexOf(prefix) === 0) { locked = LOCKED_MARKETS[prefix]; break; }
    }
    if (!locked) return;

    // Skip if user dismissed recently
    try {
      var dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
      if (dismissedAt && (Date.now() - dismissedAt) < DISMISS_TTL_MS) return;
    } catch (e) { /* localStorage unavailable - fall through */ }

    // Ask Shopify's IP-based geolocation endpoint for the visitor's real country.
    // This is the same endpoint Shopify's own Geolocation app uses.
    var root = window.Shopify.routes.root || '/';
    var url = root + 'browsing_context_suggestions.json?country[enabled]=true';

    fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        if (!d || !d.suggestions || d.suggestions.length === 0) return;
        var s = d.suggestions[0];
        if (!s || !s.parts || !s.parts.country) return;
        var cc = (s.parts.country.handle || '').toUpperCase();
        if (!cc || cc === locked.country) return; // genuinely in Japan - keep JPY

        var countryName = s.parts.country.label || cc;
        var currency = (s.parts.currency && s.parts.currency.handle) || '';
        showBanner({
          visitorCountry: cc,
          visitorCountryName: countryName,
          visitorCurrency: currency,
          lockedMarket: locked
        });
      })
      .catch(function () { /* network error - silently skip */ });
  }

  function showBanner(ctx) {
    // Inject CSS once
    if (!document.getElementById('geo-mismatch-style')) {
      var style = document.createElement('style');
      style.id = 'geo-mismatch-style';
      style.textContent = [
        '.geo-mm-banner{position:sticky;top:0;z-index:9998;background:#FFF8E1;',
        'border-bottom:1px solid rgba(251,193,6,0.45);padding:12px 16px;',
        'font-family:inherit;box-shadow:0 2px 10px rgba(0,0,0,0.06);',
        'animation:geoMmSlide .35s ease-out both;}',
        '@keyframes geoMmSlide{from{transform:translateY(-100%);}to{transform:translateY(0);}}',
        '.geo-mm-banner__inner{max-width:1200px;margin:0 auto;display:flex;',
        'align-items:center;gap:12px;flex-wrap:wrap;}',
        '.geo-mm-banner__icon{font-size:22px;flex-shrink:0;line-height:1;}',
        '.geo-mm-banner__text{flex:1;min-width:220px;font-size:14px;line-height:1.45;',
        'color:#1a1a1a;}',
        '.geo-mm-banner__text strong{font-weight:700;}',
        '.geo-mm-banner__actions{display:flex;gap:8px;align-items:center;flex-wrap:wrap;}',
        '.geo-mm-banner__btn{padding:9px 18px;border-radius:999px;border:none;',
        'cursor:pointer;font:inherit;font-size:14px;font-weight:600;white-space:nowrap;',
        'transition:all .2s ease;}',
        '.geo-mm-banner__btn--primary{background:#FBC106;color:#1a1a1a;}',
        '.geo-mm-banner__btn--primary:hover{background:#F59E0B;transform:translateY(-1px);',
        'box-shadow:0 4px 12px rgba(251,193,6,0.35);}',
        '.geo-mm-banner__btn--secondary{background:transparent;color:#374151;',
        'border:1px solid #D1D5DB;}',
        '.geo-mm-banner__btn--secondary:hover{background:#F3F4F6;}',
        '.geo-mm-banner__close{background:transparent;border:none;font-size:20px;',
        'line-height:1;cursor:pointer;color:#6B7280;padding:4px 8px;flex-shrink:0;}',
        '.geo-mm-banner__close:hover{color:#1a1a1a;}',
        '@media (max-width:640px){',
        '.geo-mm-banner{padding:12px 14px;}',
        '.geo-mm-banner__text{font-size:13px;width:100%;}',
        '.geo-mm-banner__actions{width:100%;}',
        '.geo-mm-banner__btn{flex:1;font-size:13px;padding:10px 14px;}',
        '.geo-mm-banner__close{position:absolute;right:8px;top:8px;}',
        '}'
      ].join('');
      document.head.appendChild(style);
    }

    // Build banner
    var banner = document.createElement('div');
    banner.className = 'geo-mm-banner';
    banner.setAttribute('role', 'region');
    banner.setAttribute('aria-label', 'Region suggestion');

    var inner = document.createElement('div');
    inner.className = 'geo-mm-banner__inner';

    var icon = document.createElement('span');
    icon.className = 'geo-mm-banner__icon';
    icon.textContent = '🌏';
    icon.setAttribute('aria-hidden', 'true');

    var text = document.createElement('span');
    text.className = 'geo-mm-banner__text';
    text.innerHTML =
      'Visiting from <strong>' + esc(ctx.visitorCountryName) + '</strong>? ' +
      "You're currently shopping in <strong>" + esc(ctx.lockedMarket.currency) +
      '</strong> (' + esc(ctx.lockedMarket.label) + ' prices). ' +
      'Switch for correct local pricing.';

    var actions = document.createElement('div');
    actions.className = 'geo-mm-banner__actions';

    var switchBtn = document.createElement('button');
    switchBtn.type = 'button';
    switchBtn.className = 'geo-mm-banner__btn geo-mm-banner__btn--primary';
    switchBtn.textContent = ctx.visitorCurrency
      ? 'Switch to ' + ctx.visitorCurrency
      : 'Switch to ' + ctx.visitorCountry;

    var keepBtn = document.createElement('button');
    keepBtn.type = 'button';
    keepBtn.className = 'geo-mm-banner__btn geo-mm-banner__btn--secondary';
    keepBtn.textContent = 'Keep ' + ctx.lockedMarket.currency;

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'geo-mm-banner__close';
    closeBtn.setAttribute('aria-label', 'Dismiss region banner');
    closeBtn.innerHTML = '&times;';

    actions.appendChild(switchBtn);
    actions.appendChild(keepBtn);
    inner.appendChild(icon);
    inner.appendChild(text);
    inner.appendChild(actions);
    inner.appendChild(closeBtn);
    banner.appendChild(inner);

    // Insert at the very top of <body>
    if (document.body.firstChild) {
      document.body.insertBefore(banner, document.body.firstChild);
    } else {
      document.body.appendChild(banner);
    }

    switchBtn.addEventListener('click', function () {
      submitLocalizationSwitch(ctx.visitorCountry);
    });

    keepBtn.addEventListener('click', dismiss);
    closeBtn.addEventListener('click', dismiss);

    function dismiss() {
      try { localStorage.setItem(DISMISS_KEY, Date.now().toString()); } catch (e) {}
      banner.style.transition = 'transform .25s ease, opacity .25s ease';
      banner.style.transform = 'translateY(-100%)';
      banner.style.opacity = '0';
      setTimeout(function () { if (banner.parentNode) banner.parentNode.removeChild(banner); }, 260);
    }
  }

  function submitLocalizationSwitch(countryCode) {
    // Strip the /ja-jp or /en-jp prefix so user lands on root market version
    var newPath = window.location.pathname.replace(/^\/(ja-jp|en-jp)/i, '') || '/';
    var newUrl = newPath + window.location.search + window.location.hash;

    var form = document.createElement('form');
    form.action = '/localization';
    form.method = 'POST';
    form.hidden = true;

    var method = document.createElement('input');
    method.name = '_method'; method.value = 'PUT';
    var cc = document.createElement('input');
    cc.name = 'country_code'; cc.value = countryCode;
    var ret = document.createElement('input');
    ret.name = 'return_to'; ret.value = newUrl;

    form.appendChild(method);
    form.appendChild(cc);
    form.appendChild(ret);
    document.body.appendChild(form);
    form.submit();
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[<>&"']/g, function (c) {
      return { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
